import React, { useState } from "react";
import { NavLink, useMatch, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  AppBar,
  Box,
  Divider,
  Link,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from "@mui/material";
import { AccountCircle, ArrowDropDown } from "@mui/icons-material";
import InviteUsers from "src/components/InviteUsers";
import StartApplication from "src/components/StartApplication";
import { userSelector } from "src/store/slices/userSlice";
import { logoutAndRedirectToLogin } from "src/utils/useAxios";
import { pageRoutes, userTypes } from "src/constants";
import ggfLogo from "../ggf.gif";

// Who sees the users page and invites users from it
const userManagerTypes = [userTypes.MASTER, userTypes.ADMIN];

// Top bar pages. One with userTypes is only shown to those user types
const navItems = [
  { label: "Applications", path: pageRoutes.APPLICATIONS },
  { label: "Customers", path: pageRoutes.CUSTOMERS },
  // The server only returns an admin's own organisation's users
  {
    label: "Users",
    path: pageRoutes.USERS,
    userTypes: userManagerTypes,
  },
  {
    label: "Organisations",
    path: pageRoutes.ORGANISATIONS,
    userTypes: [userTypes.MASTER],
  },
];

const accountButtonId = "account-menu-button";
const accountMenuId = "account-menu";

export default function TopMenu() {
  const navigate = useNavigate();
  const user = useSelector(userSelector);
  const isUsersPage = Boolean(useMatch(pageRoutes.USERS));
  const [anchorEl, setAnchorEl] = useState(null);

  const isMenuOpen = Boolean(anchorEl);
  const canInviteUsers =
    isUsersPage && userManagerTypes.includes(user.userType);
  const visibleNavItems = navItems.filter(
    (item) => !item.userTypes || item.userTypes.includes(user.userType)
  );

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box style={{ flexGrow: 1 }}>
      <AppBar
        position="fixed"
        elevation={1}
        style={{ zIndex: 100, background: "#fdfdfd" }}
      >
        <Toolbar>
          <img
            src={ggfLogo}
            alt="GGF"
            style={{
              height: "44px",
              imageRendering: "-webkit-optimize-contrast",
              marginRight: "30px",
            }}
          />
          <List
            component="nav"
            aria-label="Main navigation"
            style={{ display: "flex", flexDirection: "row", padding: 0 }}
          >
            {visibleNavItems.map(({ label, path }) => (
              <ListItem
                key={path}
                style={{ cursor: "pointer" }}
                onClick={() => navigate(path)}
              >
                <ListItemText
                  primary={label}
                  primaryTypographyProps={{ color: "secondary" }}
                />
              </ListItem>
            ))}
          </List>
          <Box style={{ flexGrow: 1 }} />
          {/* On the users page, inviting users takes the place of starting an application */}
          {canInviteUsers ? <InviteUsers /> : <StartApplication />}
          <Divider
            orientation="vertical"
            variant="middle"
            flexItem
            color="white"
            style={{ marginRight: "20px" }}
          />
          <Link
            id={accountButtonId}
            aria-controls={isMenuOpen ? accountMenuId : undefined}
            aria-haspopup="true"
            aria-expanded={isMenuOpen ? "true" : undefined}
            onClick={handleMenuOpen}
            color="secondary"
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              cursor: "pointer",
              textDecoration: "none",
            }}
          >
            <AccountCircle style={{ marginRight: "5px" }} />
            <Box style={{ display: "flex", flexDirection: "column" }}>
              <Box style={{ display: "flex", flexDirection: "row" }}>
                <Typography style={{ marginRight: "5px", lineHeight: 1.4 }}>
                  {user.firstName}
                </Typography>
                <Typography style={{ lineHeight: 1.4 }}>
                  {user.lastName}
                </Typography>
              </Box>
              <Typography style={{ fontSize: "10px", lineHeight: 1 }}>
                {user.organisation?.name}
              </Typography>
            </Box>
            <ArrowDropDown />
          </Link>
          <Menu
            id={accountMenuId}
            anchorEl={anchorEl}
            open={isMenuOpen}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            MenuListProps={{ "aria-labelledby": accountButtonId }}
            style={{ zIndex: 1000000000 }}
          >
            <MenuItem
              component={NavLink}
              to={pageRoutes.ACCOUNT_PROFILE}
              onClick={handleMenuClose}
            >
              Profile
            </MenuItem>
            {user.userType === userTypes.ADMIN && (
              <MenuItem
                component={NavLink}
                to={pageRoutes.ACCOUNT_ORGANISATION}
                onClick={handleMenuClose}
              >
                Organisation
              </MenuItem>
            )}
            <MenuItem
              component={NavLink}
              to={pageRoutes.ACCOUNT_SETTINGS}
              onClick={handleMenuClose}
            >
              Settings
            </MenuItem>
            {/* Full page load so the signed in user's data is cleared from the store too */}
            <MenuItem onClick={logoutAndRedirectToLogin}>Logout</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
    </Box>
  );
}
